// SKFitter lattice views: the cell of the DFT data (step 1), the neighbours a set-up includes (step 2) and the
// fitted parameters on the lattice (step 3) - the API calls and the geometry the 3D view draws (no three.js here).
import { post } from "./api";
import type { Cfg } from "./skfitter";
import type { LimitInfo } from "./transport";

export type Vec3 = [number, number, number];

export interface ViewLimits {
  "skfitter.structure_view": boolean;
  "skfitter.max_cell_atoms": number;
  "skfitter.max_shells": number;
  "skfitter.max_basis": number;
  "skfitter.view_max_atoms": number;
  "skfitter.view_max_repeat": number;
  "skfitter.view_max_bonds": number;
}

export interface ElementStyle { symbol: string; Z: number; count: number; color: string; radius: number }
export interface Site { index: number; label: string; symbol: string; frac: Vec3; cart: Vec3; eq: number; eq_label: string; moment: number | null }
export interface Bond { i: number; j: number; image: Vec3; dist: number; shell: number; label: string }

export interface StructureView {
  formula: string; n_sites: number; n_elements: number;
  lattice: [Vec3, Vec3, Vec3]; reciprocal: [Vec3, Vec3, Vec3];
  abc: Vec3; angles: Vec3; volume: number; density: number;
  spacegroup: { symbol: string; number: number | null; crystal_system: string };
  n_inequivalent: number; vacuum_axes: number[]; dimensionality: string;
  elements: ElementStyle[]; sites: Site[]; shells: number[]; d0: number | null; bonds: Bond[]; dft_code: string;
  moment_label?: string | null;     // what the moments are when not VASP's MAGMOM in μB (a recipe: "starting magnetisation")
  limit: LimitInfo | null; limits: ViewLimits;
}

export interface ShellRow {
  shell: number; dist: number; count: number; by_element: Record<string, number>; beyond: boolean;
  neighbours: { j: number; image: Vec3; label: string }[];
}
export interface Integral {
  key: string; label: string;
  v0?: number | null; eta?: number | null; value?: number | null;
  s0?: number | null; s_eta?: number | null; s_value?: number | null;
}
export interface ModelBond extends Bond { integrals: Integral[] }

export interface NeighbourView {
  n_neighbors: number; n_shells: number; cutoff: number; d0: number; shells: number[]; n_pairs: number;
  bonds: ModelBond[];
  sites: { index: number; label: string; eq: number; shells: ShellRow[]; missing_shells: number[]; n_bonds: number }[];
  inequivalent: number[]; hopping_keys: { key: string; label: string }[]; distance_decay: boolean; warnings: string[];
  limit: LimitInfo | null; limits: ViewLimits;
}

export interface OnsiteRow { orbital: string; key: string; value: number | null }
export interface SiteParams {
  index: number; label: string; symbol: string; eq_label: string;
  onsite: OnsiteRow[];
  soc: { family: string; key: string; value: number }[];
  exchange: { family: string; key: string; value: number; sign: number; site_value: number }[];
}
export interface ParamMap {
  n_neighbors: number; n_shells: number; cutoff: number; d0: number; shells: number[]; n_pairs: number; warnings: string[];
  distance_decay: boolean; hopping_keys: { key: string; label: string }[]; non_orthogonal: boolean;
  bonds: ModelBond[]; sites: SiteParams[];
  limit: LimitInfo | null; limits: ViewLimits;
}

/** Over a plan limit the neighbour / parameter calls answer only {limit, limits}. */
export type Limited<T> = T | { limit: LimitInfo; limits: ViewLimits };
export const isLimited = <T,>(v: Limited<T> | undefined): v is { limit: LimitInfo; limits: ViewLimits } =>
  !!v && !!(v as { limit: LimitInfo | null }).limit && !("sites" in (v as object));

export const structureView = (job_id: string) => post<StructureView>("/skfitter/structure", { job_id });
export const neighbourView = (job_id: string, cfg: Cfg) => post<Limited<NeighbourView>>("/skfitter/neighbours", { job_id, cfg });
export const paramMap = (job_id: string, fit_job_id: string) => post<Limited<ParamMap>>("/skfitter/parammap", { job_id, fit_job_id });

// -- geometry ------------------------------------------------------------------------------------------------------
export const add = (a: Vec3, b: Vec3): Vec3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
export const scale = (a: Vec3, s: number): Vec3 => [a[0] * s, a[1] * s, a[2] * s];

/** n1 a1 + n2 a2 + n3 a3 */
export function translate(lattice: Vec3[], n: Vec3): Vec3 {
  return [0, 1, 2].reduce<Vec3>((acc, k) => add(acc, scale(lattice[k], n[k])), [0, 0, 0]);
}

/** The atom drawn in the 3D view: a site in a cell of the supercell, or a neighbour outside the drawn cells. */
export interface DrawAtom {
  key: string; site: number; cell: Vec3; pos: Vec3; color: string; radius: number; label: string; ghost?: boolean;
  moment?: number;                 // a collinear moment, drawn as an arrow along z (up > 0, down < 0)
}
/** A bond drawn between two drawn atoms; ``half`` colours each half by its atom (the structure view). */
export interface DrawBond { key: string; a: string; b: string; from: Vec3; to: Vec3; color?: string; bond: number; shell: number }

export const cellKey = (site: number, cell: Vec3) => `${site}@${cell.join(",")}`;

export function clampRepeat(repeat: Vec3, maxRepeat: number): Vec3 {
  return repeat.map((n) => Math.max(1, Math.min(maxRepeat, Math.round(n) || 1))) as Vec3;
}

export function cells(repeat: Vec3): Vec3[] {
  const out: Vec3[] = [];
  for (let i = 0; i < repeat[0]; i++) for (let j = 0; j < repeat[1]; j++) for (let k = 0; k < repeat[2]; k++) out.push([i, j, k]);
  return out;
}

export function styleOf(sv: Pick<StructureView, "elements">): Record<string, ElementStyle> {
  return Object.fromEntries(sv.elements.map((e) => [e.symbol, e]));
}

/** Atoms of an n1 x n2 x n3 supercell (only the shown elements). */
export function supercellAtoms(sv: Pick<StructureView, "sites" | "lattice" | "elements">, repeat: Vec3, shown?: Set<string>): DrawAtom[] {
  const st = styleOf(sv);
  const out: DrawAtom[] = [];
  for (const c of cells(repeat))
    for (const s of sv.sites) {
      if (shown && !shown.has(s.symbol)) continue;
      out.push({ key: cellKey(s.index, c), site: s.index, cell: c, pos: add(s.cart, translate(sv.lattice, c)),
        color: st[s.symbol]?.color ?? "#9aa0a6", radius: st[s.symbol]?.radius ?? 1.2, label: s.label,
        ...(s.moment ? { moment: s.moment } : {}) });
    }
  return out;
}

/** Bonds of the supercell whose two ends are both drawn, out to shell ``maxShell``. */
export function supercellBonds(bonds: Bond[], atoms: DrawAtom[], maxShell: number): DrawBond[] {
  const byKey = new Map(atoms.map((a) => [a.key, a]));
  const out: DrawBond[] = [];
  bonds.forEach((b, n) => {
    if (b.shell > maxShell) return;
    for (const a of atoms) {
      if (a.site !== b.i) continue;
      const other = byKey.get(cellKey(b.j, add(a.cell, b.image)));
      if (!other) continue;
      out.push({ key: `${n}:${a.key}`, a: a.key, b: other.key, from: a.pos, to: other.pos, bond: n, shell: b.shell });
    }
  });
  return out;
}

/** How many atoms and bonds a supercell would draw (to keep within the plan's view limits before drawing). */
export function supercellCounts(sv: StructureView, repeat: Vec3, shown: Set<string>, maxShell: number): { atoms: number; bonds: number } {
  const atoms = supercellAtoms(sv, repeat, shown);
  return { atoms: atoms.length, bonds: supercellBonds(sv.bonds, atoms, maxShell).length };
}

/** The largest equal-ish repeat (n1, n2, n3 <= wanted) within the atom limit; vacuum axes stay at 1. */
export function fitRepeat(nSites: number, wanted: Vec3, maxAtoms: number, maxRepeat: number): Vec3 {
  const r = clampRepeat(wanted, maxRepeat);
  while (r[0] * r[1] * r[2] * nSites > maxAtoms) {
    const k = r.indexOf(Math.max(...r));
    if (r[k] === 1) break;
    r[k] -= 1;
  }
  return r;
}

/** A default supercell: 3 x 3 in the plane of a 2D cell; otherwise 2 along every lattice vector shorter than 7 Å
 * (so short axes show their periodicity, long ones - a layered c axis - stay single); within the plan's atom limit. */
export function defaultRepeat(sv: StructureView): Vec3 {
  const base = (sv.vacuum_axes.length ? [3, 3, 3] : sv.abc.map((l) => (l < 7 ? 2 : 1))) as Vec3;
  for (const ax of sv.vacuum_axes) base[ax] = 1;
  return fitRepeat(sv.n_sites, base, sv.limits["skfitter.view_max_atoms"], sv.limits["skfitter.view_max_repeat"]);
}

/** The bonds of a model drawn around the home cell: every site of the cell, and each bond's partner (ghost when it
 * sits in another cell). With ``centre`` only that site's bonds. */
export function modelScene(sv: Pick<StructureView, "sites" | "lattice" | "elements">, bonds: Bond[], centre: number | null,
  shellColor: (shell: number) => string): { atoms: DrawAtom[]; bonds: DrawBond[] } {
  const st = styleOf(sv);
  const atoms = new Map<string, DrawAtom>();
  const put = (site: number, cell: Vec3, ghost: boolean) => {
    const key = cellKey(site, cell);
    if (!atoms.has(key)) {
      const s = sv.sites[site];
      atoms.set(key, { key, site, cell, pos: add(s.cart, translate(sv.lattice, cell)), color: st[s.symbol]?.color ?? "#9aa0a6",
        radius: st[s.symbol]?.radius ?? 1.2, label: s.label, ghost });
    }
    return atoms.get(key)!;
  };
  if (centre == null) sv.sites.forEach((s) => put(s.index, [0, 0, 0], false));
  else put(centre, [0, 0, 0], false);
  const out: DrawBond[] = [];
  bonds.forEach((b, n) => {
    const ends: [number, number, Vec3][] = [];
    if (centre == null || b.i === centre) ends.push([b.i, b.j, b.image]);
    if (centre != null && b.j === centre && !(b.i === centre)) ends.push([b.j, b.i, scale(b.image, -1) as Vec3]);
    if (centre != null && b.i === centre && b.j === centre) ends.push([b.i, b.i, scale(b.image, -1) as Vec3]);
    for (const [i, j, img] of ends) {
      const a = put(i, [0, 0, 0], false);
      const o = put(j, img, centre == null && !(img[0] === 0 && img[1] === 0 && img[2] === 0));
      out.push({ key: `${n}:${i}:${img.join(",")}`, a: a.key, b: o.key, from: a.pos, to: o.pos, color: shellColor(b.shell),
        bond: n, shell: b.shell });
    }
  });
  return { atoms: [...atoms.values()], bonds: out };
}

// -- text -------------------------------------------------------------------------------------------------------------
export const SHELL_COLORS = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#4a3aa7", "#e34948",
  "#00a3b8", "#8c564b", "#7f7f7f", "#bcbd22", "#17becf", "#9467bd", "#d62728", "#2ca02c"];
export const shellColor = (shell: number) => SHELL_COLORS[(shell - 1) % SHELL_COLORS.length];

export const num = (v: number | null | undefined, digits = 4) => (v == null || !Number.isFinite(v) ? "–" : v.toFixed(digits));
export const vec = (v: number[], digits = 4) => `(${v.map((x) => x.toFixed(digits)).join(", ")})`;

/** "3 × N" or "3 × B, 6 × N" */
export function countText(byElement: Record<string, number>): string {
  return Object.entries(byElement).map(([e, n]) => `${n} × ${e}`).join(", ");
}

/** "B1 - shell 1: 3 × N at 1.446 Å; shell 2: 6 × B at 2.504 Å" */
export function siteLine(s: NeighbourView["sites"][number]): string {
  return `${s.label} - ` + s.shells.map((r) => `shell ${r.shell}: ${countText(r.by_element)} at ${r.dist.toFixed(3)} Å${r.beyond ? " (by the 0.1 Å margin)" : ""}`).join("; ");
}

/** Subscript the orbital names of the parameter keys a little: 'e_orbit0_C_pz' stays as it is (monospace). */
export function familyName(f: string): string {
  return { s: "s", p: "p", d: "d", f: "f" }[f] ?? f;
}

/** The parameter keys (as in the fitted-parameters table) a bond uses: V0 keys, η keys, overlaps. */
export function bondParamKeys(b: ModelBond, decay: boolean, nonOrth: boolean): string[] {
  const out: string[] = [];
  for (const it of b.integrals) {
    const bt = it.key.split("_")[1];
    out.push(`${it.key}_0`);
    if (decay) out.push(`eta_${bt}`);
    if (nonOrth) { out.push(`s_${it.key.slice(2)}_0`); if (decay) out.push(`eta_s_${bt}`); }
  }
  return out;
}

export function siteParamKeys(s: SiteParams): string[] {
  return [...s.onsite.map((r) => r.key), ...s.soc.map((r) => r.key), ...s.exchange.map((r) => r.key)];
}
